import { useState } from "react";
import { Box, Divider, Grid, TextField } from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import EmailAddressTextField from "../../../components/EmailAddressField";
import AuditInfoLabel from "../../../components/AuditInfoLabel";
import { GlobalButton, GlobalSecondaryButton } from "../../styles";
import SupplierApi, { Supplier, SupplierPayload } from "../../../api/ChemInv/Supplier";

type SupplierFormValues = Omit<SupplierPayload, "certificationDate"> & {
  certificationDate: Date | null;
};

const supplierValidationSchema = Yup.object().shape({
  supplierName: Yup.string()
    .transform((v) => (v ? v.trim() : ""))
    .required("Supplier Name is required")
    .max(100, "Must be 100 characters or less"),
  description: Yup.string().nullable(),
  supplierLocation: Yup.string().nullable().max(128, "Must be 128 characters or less"),
  certificationDate: Yup.date().nullable().typeError("Invalid date"),
  certifiedByEmail: Yup.string().nullable().max(60, "Must be 60 characters or less"),
});

type SupplierFormProps = {
  supplier: Supplier | null;
  isNew: boolean;
  canEdit: boolean;
  onSave: () => void;
  onCancel: () => void;
  onError?: (message: string) => void;
};

export const SupplierForm = ({
  supplier,
  isNew,
  canEdit,
  onSave,
  onCancel,
  onError,
}: SupplierFormProps) => {
  const [certifiedByEmailError, setCertifiedByEmailError] = useState<boolean | null>(null);

  const isEditing = !isNew && supplier !== null;

  const initialValues: SupplierFormValues = {
    supplierName: supplier?.supplierName ?? "",
    description: supplier?.description ?? "",
    supplierLocation: supplier?.supplierLocation ?? "",
    certificationDate: supplier?.certificationDate
      ? new Date(supplier.certificationDate)
      : null,
    certifiedByEmail: supplier?.certifiedByEmail ?? "",
  };

  const handleSubmit = async (
    values: SupplierFormValues,
    helpers: FormikHelpers<SupplierFormValues>,
  ) => {
    const payload: SupplierPayload = {
      ...values,
      certificationDate: values.certificationDate
        ? values.certificationDate.toISOString()
        : null,
    };
    try {
      if (isEditing) {
        await SupplierApi.update(payload);
      } else {
        await SupplierApi.create(payload);
      }
      onSave();
    } catch {
      const message = isEditing
        ? "Failed to update supplier. Please try again."
        : "Failed to create supplier. Please try again.";
      onError?.(message);
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Formik<SupplierFormValues>
      initialValues={initialValues}
      enableReinitialize
      validationSchema={supplierValidationSchema}
      onSubmit={handleSubmit}>
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, dirty }) => (
        <Form style={{ margin: "10px", width: "40rem" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="* Supplier Name"
                size="small"
                fullWidth
                value={values.supplierName}
                InputLabelProps={{ shrink: true }}
                disabled
                inputProps={{ readOnly: true }}
                helperText=" "
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="supplierLocation"
                label="Supplier Location"
                placeholder="Supplier Location"
                size="small"
                fullWidth
                value={values.supplierLocation}
                onChange={handleChange}
                onBlur={handleBlur}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 128 }}
                disabled={!canEdit}
                error={touched.supplierLocation && Boolean(errors.supplierLocation)}
                helperText={(touched.supplierLocation && errors.supplierLocation) ?? " "}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                placeholder="Description"
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                InputLabelProps={{ shrink: true }}
                disabled={!canEdit}
                error={touched.description && Boolean(errors.description)}
                helperText={(touched.description && errors.description) ?? " "}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Certification Date"
                  value={values.certificationDate ?? null}
                  onChange={(date: Date | null) =>
                    setFieldValue("certificationDate", date ?? null)
                  }
                  disabled={!canEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={touched.certificationDate && Boolean(errors.certificationDate)}
                      helperText={
                        (touched.certificationDate && errors.certificationDate
                          ? String(errors.certificationDate)
                          : undefined) ?? " "
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <EmailAddressTextField
                key="certifiedByEmail"
                validatedUserEmail={values.certifiedByEmail}
                setValidatedUserEmail={(value: string) => {
                  setFieldValue("certifiedByEmail", value);
                }}
                setHasErrors={(hasErrors) => {
                  setCertifiedByEmailError(hasErrors);
                }}
                isDisabled={!canEdit}
                labelText="Certified By Email"
                placeholderText="Certified By Email"
                size="small"
                margin="none"
                fieldWidth="100%"
                showPlusMeButton
                isRequired={false}
              />
            </Grid>
            {canEdit && (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                  <GlobalButton
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || certifiedByEmailError === true || (isEditing && !dirty)}>
                    Save
                  </GlobalButton>
                  <GlobalSecondaryButton type="button" variant="contained" onClick={onCancel}>
                    Cancel
                  </GlobalSecondaryButton>
                </Box>
              </Grid>
            )}
            {isEditing && supplier && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <AuditInfoLabel auditObject={supplier} />
              </Grid>
            )}
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
